import { useState, useMemo } from "react";
import dayjs from "dayjs";
import { useHistory } from "react-router-dom";
import relativeTime from "dayjs/plugin/relativeTime";
import { Bar } from "react-chartjs-2";
import {
  Tooltip,
  BarElement,
  LinearScale,
  CategoryScale,
  Chart as ChartJS,
} from "chart.js";

import { useQuery } from "@tanstack/react-query";
import {
  Flex,
  Label,
  Table,
  Button,
  Select,
  Spinner,
  TableRow,
  NoResults,
  TableBody,
  TableCell,
  TableHead,
  FilterMenu,
  PageWrapper,
  StretchPanel,
  TableHeadCell,
  useNotification,
  useFilterReducer,
} from "@ubisend/pulse-components";

import { useAuth } from "../hooks";
import { refetchReportMetric, exportReportMetric } from "../api";

dayjs.extend(relativeTime);
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const METRIC_KEYS = [
  { key: "conversations-triggered", label: "Conversations Triggered" },
  { key: "faqs-triggered", label: "FAQs Triggered" },
  { key: "generative-ai-triggered", label: "AI Triggered" },
  { key: "subscribers-created", label: "Subscribers Created" },
  { key: "active-subscribers", label: "Active Subscribers" },
  { key: "total-live-chats", label: "Live Chats" },
  { key: "active-live-chats", label: "Active Live Chats" },
];

const ReportMetric = ({ match: { params } }) => {
  const history = useHistory();
  const { user } = useAuth();

  const currentMonth = dayjs().format("MMMM");
  const currentYear = dayjs().year();
  const { showSuccess, showError } = useNotification();
  const [exportingType, setExportingType] = useState(null); // 'summary' or 'detailed'

  // Generate year options for the past 4 years
  const yearOptions = Array.from(
    { length: 4 },
    (_, i) => currentYear - (3 - i),
  );

  const filters = useFilterReducer({
    id: "reportMetrics",
    initialFilters: {
      year: currentYear,
      month: currentMonth,
      clients: [],
    },
  });

  const queryParams = {
    year: filters.form?.year ?? currentYear,
    month: filters.form?.month ?? currentMonth,
    clients: filters.form?.clients ?? [],
  };

  const { data, isLoading } = useQuery({
    queryKey: ["reports", params.id, queryParams],
    queryFn: () =>
      refetchReportMetric({
        clientId: params.id,
        params: {
          type: "summary",
          year: queryParams.year,
          month: queryParams.month,
          clients: queryParams.clients,
        },
      }),
    enabled: !!queryParams.year && !!queryParams.month,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const clientsArr = useMemo(() => {
    return Array.isArray(data?.data?.clients)
      ? data.data.clients
      : Object.values(data?.data?.clients ?? {});
  }, [data?.data?.clients]);

  // Build options from clients
  const clientOptions = clientsArr.map((c) => ({
    label: c.name,
    value: c.id,
  }));

  const summaryTotals = useMemo(() => {
    if (!clientsArr.length) return {};
    return {
      conversations: clientsArr.reduce(
        (sum, c) => sum + (c?.metrics?.["conversations-triggered"] || 0),
        0,
      ),
      faqs: clientsArr.reduce(
        (sum, c) => sum + (c?.metrics?.["faqs-triggered"] || 0),
        0,
      ),
      generativeai: clientsArr.reduce(
        (sum, c) => sum + (c?.metrics?.["generative-ai-triggered"] || 0),
        0,
      ),
      subscribers: clientsArr.reduce(
        (sum, c) => sum + (c?.metrics?.["subscribers-created"] || 0),
        0,
      ),
      activeSubs: clientsArr.reduce(
        (sum, c) => sum + (c?.metrics?.["active-subscribers"] || 0),
        0,
      ),
    };
  }, [clientsArr]);

  // Memoize chart data to avoid recalculating on every render
  const chartDatasets = useMemo(() => {
    const clientColors = {};
    clientsArr.forEach((client, i) => {
      clientColors[client.name] = `hsl(${(i * 60) % 360}, 70%, 50%)`;
    });

    return METRIC_KEYS.map(({ key, label }) => ({
      key,
      label,
      data: {
        labels: clientsArr.map((client) => client.name),
        datasets: [
          {
            data: clientsArr.map((client) => client?.metrics?.[key] || 0),
            backgroundColor: clientsArr.map(
              (client) => clientColors[client.name],
            ),
            borderRadius: 4,
            barThickness: clientsArr.length === 1 ? 40 : 20,
          },
        ],
      },
    }));
  }, [clientsArr]);

  const handleExport = async (type) => {
    setExportingType(type);
    try {
      await exportReportMetric({
        clientId: params.id,
        params: {
          userId: user.id,
          type,
          ...queryParams,
        },
      });
      showSuccess(
        `You will receive an email shortly with details on accessing your export.`,
      );
    } catch (error) {
      showError("Export failed:", error);
    } finally {
      setExportingType(null);
    }
  };

  const SummaryCard = ({ title, value }) => (
    <div className="summary-card">
      <div className="summary-value">{value ?? 0}</div>
      <div className="summary-title">{title}</div>
    </div>
  );

  return (
    <PageWrapper
      header={`${data?.data?.install_name ?? "Reporting Metrics"}`}
      actions={
        <Flex xSpace>
          <FilterMenu buttonProps={{ loading: isLoading }} {...filters.props}>
            <Flex style={{ width: "20rem" }} col>
              <Flex col fat mb>
                <Label htmlFor="year">Year</Label>
                <Select
                  id="year"
                  placeholder="Select year"
                  options={yearOptions.map((y) => ({
                    label: y,
                    value: y,
                  }))}
                  value={
                    filters.filters?.year
                      ? {
                          label: filters.filters.year,
                          value: filters.filters.year,
                        }
                      : { label: currentYear, value: currentYear }
                  }
                  onChange={(selected) => {
                    filters.updateFilters({ year: selected.value });
                  }}
                />
              </Flex>
              <Flex col fat mb>
                <Label htmlFor="month">Month</Label>
                <Select
                  id="month"
                  placeholder="Select month"
                  options={MONTHS.map((m) => ({ label: m, value: m }))}
                  value={
                    filters.filters?.month
                      ? {
                          label: filters.filters.month,
                          value: filters.filters.month,
                        }
                      : { label: currentMonth, value: currentMonth }
                  }
                  onChange={(selected) =>
                    filters.updateFilters({ month: selected.value })
                  }
                />
              </Flex>
              <Flex col fat>
                <Label htmlFor="clients">Clients</Label>
                <Select
                  id="clients"
                  placeholder="Select clients"
                  options={clientOptions}
                  value={clientOptions.filter((opt) =>
                    (filters.filters?.clients || []).includes(opt.value),
                  )}
                  onChange={(selected) =>
                    filters.updateFilters({
                      clients: Array.isArray(selected)
                        ? selected.map((v) => v.value)
                        : [],
                    })
                  }
                  isMulti
                />
              </Flex>
            </Flex>
          </FilterMenu>

          <Button
            icon="arrowNarrowLeft"
            variant="primary"
            onClick={() => history.goBack()}
          >
            Back
          </Button>
        </Flex>
      }
    >
      {/* Summary Cards */}
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="summary-grid">
          <SummaryCard
            title="Total Conversations Triggered"
            value={summaryTotals.conversations}
          />
          <SummaryCard
            title="Total FAQs Triggered"
            value={summaryTotals.faqs}
          />
          <SummaryCard
            title="Total Generative AI Triggered"
            value={summaryTotals.generativeai}
          />
          <SummaryCard
            title="Total Subscribers Created"
            value={summaryTotals.subscribers}
          />
          <SummaryCard
            title="Total Active Subscribers"
            value={summaryTotals.activeSubs}
          />
        </div>
      )}

      {/* Bar Chart Section */}
      {!isLoading && data && (
        <div className="barchart-grid">
          {chartDatasets.map(({ key, label, data: chartData }) => {
            const chartOptions = {
              responsive: true,
              maintainAspectRatio: false,
              indexAxis: "y",
              plugins: { legend: { display: false } },
              scales: {
                x: { beginAtZero: true, ticks: { precision: 0, stepSize: 1 } },
                y: { ticks: { autoSkip: false } },
              },
            };

            const chartHeight = Math.max(clientsArr.length * 40 + 100, 250);

            return (
              <div
                key={key}
                className="barchart-card"
                style={{ height: `${chartHeight}px` }}
              >
                <h4 className="barchart-title">{label}</h4>
                <div className="barchart-inner">
                  <Bar data={chartData} options={chartOptions} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Table Section */}
      <StretchPanel>
        <Table loading={isLoading} loadingColumns={6}>
          {!isLoading && data && (
            <>
              {clientsArr.length === 0 && <NoResults />}
              {clientsArr.length > 0 && (
                <TableHead>
                  <TableRow>
                    <TableHeadCell>Client</TableHeadCell>
                    <TableHeadCell>Conversations</TableHeadCell>
                    <TableHeadCell>FAQs</TableHeadCell>
                    <TableHeadCell>AI Triggered</TableHeadCell>
                    <TableHeadCell>Subscribers</TableHeadCell>
                    <TableHeadCell>Active Subs</TableHeadCell>
                    <TableHeadCell>Live Chats</TableHeadCell>
                    <TableHeadCell>Active Live Chats</TableHeadCell>
                  </TableRow>
                </TableHead>
              )}
              <TableBody>
                {clientsArr.map((client, key) => (
                  <TableRow key={key}>
                    <TableCell>{client.name}</TableCell>
                    <TableCell>
                      {client?.metrics?.["conversations-triggered"] ?? 0}
                    </TableCell>
                    <TableCell>
                      {client?.metrics?.["faqs-triggered"] ?? 0}
                    </TableCell>
                    <TableCell>
                      {client?.metrics?.["generative-ai-triggered"] ?? 0}
                    </TableCell>
                    <TableCell>
                      {client?.metrics?.["subscribers-created"] ?? 0}
                    </TableCell>
                    <TableCell>
                      {client?.metrics?.["active-subscribers"] ?? 0}
                    </TableCell>
                    <TableCell>
                      {client?.metrics?.["total-live-chats"] ?? 0}
                    </TableCell>
                    <TableCell>
                      {client?.metrics?.["active-live-chats"] ?? 0}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </>
          )}
        </Table>
      </StretchPanel>

      {/* Export Buttons */}
      {!isLoading && data && (
        <div className="flex-end-row">
          <Button
            disabled={exportingType !== null}
            loading={exportingType === "summary"}
            onClick={() => handleExport("summary")}
          >
            Export Summary (CSV)
          </Button>
          <Button
            disabled={exportingType !== null}
            icon="download"
            loading={exportingType === "detailed"}
            onClick={() => handleExport("detailed")}
          >
            Export Detailed (Excel)
          </Button>
        </div>
      )}
    </PageWrapper>
  );
};

export default ReportMetric;
